import { Button, Loader } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import EvStationPropType from '../../prop-types/evStation';
import EvStationListItem from '../EvStationListItem/EvStationListItem';
import styles from './EvStationList.module.css';

const cx = classnames.bind(styles);

export default function EvStationList({
  stations,
  isLoading,
  hasMore,
  onLoadMore,
  onStationClick,
  selectedStationId,
  highlightedStationId,
  onStationHover,
}) {
  const listRef = useRef(null);
  const selectedRef = useRef(null);

  // Scroll to selected station when it changes
  useEffect(() => {
    if (selectedStationId && selectedRef.current && listRef.current) {
      selectedRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [selectedStationId]);

  if (isLoading && stations.length === 0) {
    return (
      <div className={cx('loadingContainer')}>
        <Loader size="lg">Loading EV stations...</Loader>
      </div>
    );
  }

  if (!isLoading && stations.length === 0) {
    return (
      <div className={cx('noResults')}>
        <p className={cx('noResultsTitle')}>No EV charging stations found</p>
        <p className={cx('noResultsMessage')}>
          Try adjusting your search location or filters.
        </p>
      </div>
    );
  }

  return (
    <div className={cx('stationList')} ref={listRef}>
      {stations.map((station, index) => {
        const isSelected = selectedStationId === station.id;
        const isHighlighted = highlightedStationId === station.id;

        return (
          <div
            key={station.id}
            ref={isSelected ? selectedRef : null}
          >
            <EvStationListItem
              station={station}
              isSelected={isSelected}
              isHighlighted={isHighlighted}
              onClick={() => onStationClick?.(station, index)}
              onMouseEnter={() => onStationHover?.(station)}
              onMouseLeave={() => onStationHover?.(null)}
            />
          </div>
        );
      })}

      {hasMore && (
        <div className={cx('loadMoreContainer')}>
          <Button
            onClick={onLoadMore}
            disabled={isLoading}
            variant="secondary"
          >
            {isLoading ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  );
}

EvStationList.propTypes = {
  stations: PropTypes.arrayOf(EvStationPropType),
  isLoading: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  onStationClick: PropTypes.func,
  selectedStationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  highlightedStationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onStationHover: PropTypes.func,
};

EvStationList.defaultProps = {
  stations: [],
  isLoading: false,
  hasMore: false,
  onLoadMore: undefined,
  onStationClick: undefined,
  selectedStationId: null,
  highlightedStationId: null,
  onStationHover: undefined,
};
